import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import {
    GC_ORDER_STATUS_POLLING_INTERVAL,
    INITIAL_GC_ORDER_STATUS_POLL_DELAY,
} from "@/shared/constants";
import {
    SDK_STATUSES,
    CHECKOUT_FIELDS,
    POLLING_STATUS_CODES,
    ORDER_STATUSES,
} from "@/shared/gift-cards.constants";
import { getParam } from "@/shared/helpers/url-helper";

import { processOrder } from "@/utils/afterpay-checkout";

import { getOrderStatus } from "@/pages/api/store-directory/gift-cards";

import { useErrorMessage } from "@/context/ErrorMessageContext";
import { useCurrentLocale } from "@/context/LanguageContext";

import { ACCENT_COLORS } from "@/components/component.constants";
import SplitterNumbers from "@/components/splitters/splitter-numbers/splitter-numbers";

import { useIsArcade } from "@/hooks/useIsArcade";

import {
    Consumer,
    GiftCardCheckout,
    GiftCardProps,
    OrderStatus,
    Recipient,
} from "@/interfaces/gift-cards/gift-card-interface";
import { transformCheckout } from "@/interfaces/gift-cards/gift-card-transform";

import AlertCircleIconArcade from "../../assets/icons/alert-circle-arcade.svg";
import { GiftCardCheckoutNotification } from "./gift-card-checkout-notification";
import GiftCardDetailsLayout from "./gift-card-details-layout";
import PaymentConfirmationLayout from "./payment-confirmation-layout";

const SHOW_CLOSE_BUTTON = "showCloseButton";

const initialConsumer = {
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    confirmEmail: "",
};
const initialRecipient = {
    name: "",
    phone: "",
    email: "",
    confirmEmail: "",
    message: "",
};

type Props = {
    giftCard: GiftCardProps;
};
const GiftCardLayout: React.FC<Props> = ({ giftCard }) => {
    const { t } = useTranslation();
    const sectionOne = {
        headline: t("giftCards.howItWorks.sectionOne.headline"),
        body: "",
    };
    const sectionTwo = {
        headline: t("giftCards.howItWorks.sectionTwo.headline"),
        body: "",
    };
    const sectionThree = {
        headline:
            giftCard.supplier === "NBI"
                ? t("giftCards.howItWorks.sectionThree.headlineNBI")
                : t("giftCards.howItWorks.sectionThree.headline"),
        body: "",
    };

    const howItWorksSections = [sectionOne, sectionTwo, sectionThree];
    const [consumer, setConsumer] = useState<Consumer>(initialConsumer);
    const [recipient, setRecipient] = useState<Recipient>(initialRecipient);
    const [orderStatus, setOrderStatus] = useState<OrderStatus>(null);
    const [orderId, setOrderId] = useState<number>(null);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [paramStatus, setParamStatus] = useState<string>("");
    const [paramToken, setParamToken] = useState<string>("");
    const [isPurchaseFlowComplete, setIsPurchaseFlowComplete] = useState<boolean>(false);
    const { createErrorMessage } = useErrorMessage();
    const [showErrorOverlay, setShowErrorOverlay] = useState<boolean>(false);

    const router = useRouter();
    const shouldSkipLogin = router.query.skip_login === "true";

    const locale = useCurrentLocale();
    const GIFT_CARD_ERROR_MESSAGES = {
        OUT_OF_STOCK: t("messages.outOfStock"),
        INVALID_RECIPIENT_EMAIL: t("messages.invalidEmail"),
        CANCELLED: t("messages.cancelledOrder"),
    };
    const isArcade = useIsArcade();
    /*
     * When using Safari (desktop or iOS) the Afterpay SDK redirects the user to portal.afterpay.com instead of
     * opening a popup and on afterpay.com.
     * Once portal.afterpay.com redirects the user back to the gift card page it will add status and orderToken
     * query parameters the the URL.
     * The state and context are reset, but the status and orderToken query parameters let us resume the checkout.
     * Reading the checkout data from session storage allows us to continue the checkout process after the user is
     * redirected back to the gift card page.
     * */
    useEffect(() => {
        const paramStatus = getParam("status", window.location.href);
        const paramOrderToken = getParam("orderToken", window.location.href);

        setParamStatus(paramStatus);
        setParamToken(paramOrderToken);
    }, [locale]);

    useEffect(() => {
        if (!paramStatus || !paramToken) {
            return;
        }

        if (paramStatus === SDK_STATUSES.CANCELLED) {
            createErrorMessage(GIFT_CARD_ERROR_MESSAGES.CANCELLED);
            return;
        }
        let checkoutData: GiftCardCheckout;
        try {
            checkoutData = JSON.parse(sessionStorage.getItem(CHECKOUT_FIELDS));
        } catch {
            checkoutData = null;
        }
        if (checkoutData) {
            if (checkoutData.consumer) {
                setConsumer({
                    ...checkoutData.consumer,
                    confirmEmail: checkoutData.consumer.email,
                });
            }

            if (checkoutData.recipient) {
                setRecipient({
                    ...checkoutData.recipient,
                    confirmEmail: checkoutData.recipient.email,
                });
            }
            processOrder(paramToken, transformCheckout(checkoutData), locale,)
                .then((orderId) => setOrderId(orderId))
                .catch(() => createErrorMessage(GIFT_CARD_ERROR_MESSAGES.CANCELLED));
        }
    }, [paramStatus, paramToken, locale]); // eslint-disable-line
    // react-hooks/exhaustive-deps

    useEffect(() => {
        if (orderId === null) {
            return;
        }
        const pollOrderStatus = async () => {
            try {
                const orderStatus = await getOrderStatus(orderId, locale);
                setOrderStatus(orderStatus);

                if (POLLING_STATUS_CODES.includes(orderStatus.orderStatus)) {
                    return new Promise((resolve) =>
                        setTimeout(resolve, GC_ORDER_STATUS_POLLING_INTERVAL)
                    ).then(pollOrderStatus);
                }
            } catch (err) {
                // Mark order status as error
                setOrderStatus({ ...orderStatus, orderStatus: ORDER_STATUSES.FULFILLMENT_ERROR });
                setIsPurchaseFlowComplete(true);

                if (shouldSkipLogin) {
                    setShowErrorOverlay(true);
                } else {
                    createErrorMessage(GIFT_CARD_ERROR_MESSAGES.CANCELLED);
                }
            }
        };

        setTimeout(() => {
            pollOrderStatus();
        }, INITIAL_GC_ORDER_STATUS_POLL_DELAY);
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!orderStatus) {
            return;
        }
        if (orderStatus.orderStatus === ORDER_STATUSES.FULFILLMENT_ERROR) {
            setShowConfirmation(false);
            return;
        }
        if (orderStatus.errorCode !== null) {
            setShowConfirmation(false);
            return;
        }
        // NBI does not have an order confirmation number. It only has a transaction id
        if (orderStatus.orderNumber === null && giftCard.supplier !== "NBI") {
            setShowConfirmation(false);
            return;
        }
        if (POLLING_STATUS_CODES.includes(orderStatus.orderStatus)) {
            setShowConfirmation(false);
            return;
        }
        setShowConfirmation(true);
    }, [
        giftCard,
        giftCard?.supplier,
        orderStatus,
        orderStatus?.orderNumber,
        orderStatus?.orderStatus,
        orderStatus?.errorCode,
    ]);

    if (showErrorOverlay) {
        window["AfterpayReactNativeWebView"]?.postMessage(
            JSON.stringify({ type: SHOW_CLOSE_BUTTON, payload: {} })
        );

        return (
            <>
                <GiftCardCheckoutNotification
                    icon={isArcade && <AlertCircleIconArcade />}
                    headerText={t("giftCards.errorOverlay.header")}
                    messageText={t("giftCards.errorOverlay.message")}
                    isError
                />
            </>
        );
    }

    const showHeroOnlyVariant = ["WALMART", "SAM_S_CLUB_USD", "MACYS"].includes(
        giftCard?.sku || giftCard?.code
    );

    return (
        <>
            {showConfirmation ? (
                <PaymentConfirmationLayout
                    paymentId={orderStatus.paymentId}
                    orderNumber={orderStatus.orderNumber}
                    email={recipient.email ? recipient.email : consumer.email}
                    sku={giftCard.sku}
                    variant={shouldSkipLogin ? "short" : "default"}
                />
            ) : (
                <>
                    <GiftCardDetailsLayout
                        giftCard={giftCard}
                        consumer={consumer}
                        recipient={recipient}
                        setShowErrorOverlay={setShowErrorOverlay}
                        setConsumer={setConsumer}
                        setRecipient={setRecipient}
                        pollingStatus={orderStatus?.orderStatus}
                        setOrderId={setOrderId}
                        isPurchaseFlowComplete={isPurchaseFlowComplete}
                        setPurchaseFlowComplete={setIsPurchaseFlowComplete}
                        showHeroOnlyVariant={showHeroOnlyVariant}
                    />

                    {!showHeroOnlyVariant && (
                        <SplitterNumbers
                            title={t("giftCards.howItWorks.title")}
                            subtitle=""
                            accentColor={ACCENT_COLORS.ORCA_PASTEL}
                            fullWidth={false}
                            sections={howItWorksSections}
                            showNumbers={true}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default GiftCardLayout;
